import { create } from "zustand";
import type { CanvasObject, CanvasOp } from "@refinement-canvas/domain";

// キャンバスのドキュメント状態（オブジェクト一覧）を管理する Zustand store。
// サーバ権威のスナップショットと、自分が送ったがまだ ack されていない inFlightOps を分離保持する。
// UI 表示は両者の合成ビューで行う（applyInFlight）。

interface CanvasDocState {
  canvasId: string | null;
  serverSeq: number;
  /** サーバ確定スナップショット */
  objects: Record<string, CanvasObject>;
  /** ローカルで先行適用した op（ack 待ち） */
  inFlightOps: CanvasOp[];

  loadSnapshot: (params: {
    canvasId: string;
    serverSeq: number;
    objects: CanvasObject[];
  }) => void;
  applyOptimistic: (op: CanvasOp) => void;
  /** サーバから op.commit を受信したときに呼ぶ */
  onServerCommit: (op: CanvasOp, serverSeq: number) => void;
  /** サーバから op.reject を受信したときに呼ぶ（自分の op が拒否） */
  onServerReject: (clientOpId: string) => void;
  /** 接続切断などで完全リセットするとき */
  reset: () => void;
}

export const useCanvasDocStore = create<CanvasDocState>((set) => ({
  canvasId: null,
  serverSeq: 0,
  objects: {},
  inFlightOps: [],

  loadSnapshot: ({ canvasId, serverSeq, objects }) =>
    set({
      canvasId,
      serverSeq,
      objects: Object.fromEntries(objects.map((o) => [o.id, o])),
      inFlightOps: [],
    }),

  applyOptimistic: (op) =>
    set((state) => ({
      inFlightOps: [...state.inFlightOps, op],
      objects: applyOpToObjects(state.objects, op),
    })),

  onServerCommit: (op, serverSeq) =>
    set((state) => {
      // 自分が送った op の ack（clientOpId 一致）なら inFlightOps から取り除き、
      // サーバ確定値に置き換える。他者の op はそのまま objects に反映する。
      const remaining = state.inFlightOps.filter((p) => p.clientOpId !== op.clientOpId);
      return {
        serverSeq,
        objects: applyOpToObjects(state.objects, op),
        inFlightOps: remaining,
      };
    }),

  onServerReject: (clientOpId) =>
    set((state) => {
      const target = state.inFlightOps.find((p) => p.clientOpId === clientOpId);
      if (!target) return state;
      // 簡略化: 拒否された op を inFlight から外し、確定スナップショットを再構成する。
      // 実装フェーズではより精緻な巻き戻しを行う。
      return {
        inFlightOps: state.inFlightOps.filter((p) => p.clientOpId !== clientOpId),
      };
    }),

  reset: () => set({ canvasId: null, serverSeq: 0, objects: {}, inFlightOps: [] }),
}));

/**
 * 単一 op を objects 連想配列に適用する。
 * サーバ権威モデルでは、同じ関数を server / client の両方で適用することで決定論的に状態を再現する。
 */
function applyOpToObjects(
  objects: Record<string, CanvasObject>,
  op: CanvasOp
): Record<string, CanvasObject> {
  switch (op.type) {
    case "object.create":
      return { ...objects, [op.object.id]: op.object };
    case "object.delete": {
      const next = { ...objects };
      delete next[op.objectId];
      return next;
    }
    case "object.transform": {
      const current = objects[op.objectId];
      if (!current) return objects;
      return {
        ...objects,
        [op.objectId]: { ...current, ...op.transform },
      };
    }
    case "object.text.update": {
      const current = objects[op.objectId];
      if (!current) return objects;
      return {
        ...objects,
        [op.objectId]: { ...current, text: op.text } as CanvasObject,
      };
    }
    case "pen.stroke.commit":
      return { ...objects, [op.object.id]: op.object };
    case "object.attachment.update": {
      const current = objects[op.objectId];
      if (!current) return objects;
      // image / video / link-card に共通する src/imageUrl 系のフィールドを更新する
      if (current.type === "image" || current.type === "video") {
        return {
          ...objects,
          [op.objectId]: { ...current, src: op.src } as CanvasObject,
        };
      }
      if (current.type === "link-card") {
        return {
          ...objects,
          [op.objectId]: { ...current, imageUrl: op.src } as CanvasObject,
        };
      }
      return objects;
    }
    case "canvas.meta.update":
      return objects;
    default:
      return objects;
  }
}
